import { RescanOptions, RescanPayload, RescanResult, ScanResult, ScanResults, TargetListResponse, ScanProfile, ScanScheduleResults, ScanNodeResult } from '../../app/modules/types/scan'
import { OWNS_COMPONENT_REFUSAL } from '../ownsErrors'
import { OptionsType } from '../../app/modules/types/filters'
import { apiClient } from '../client'

/**
 * Scans API endpoints
 */
export const scansApi = {
  /**
   * Get scans list with optional filters
   */
  getAll: async (params?: string): Promise<ScanResults> => {
    const url = params ? `/scan/?${params}` : '/scan/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get scan by ID
   */
  getById: async (scanId: string, signal?: AbortSignal): Promise<ScanResult> => {
    const { data } = await apiClient.get(`/scan/${scanId}/`, { signal })
    return data
  },

  /**
   * What a re-scan of this scan could run: the recorded workflow version
   * (`same`), the workflow as it is now (`latest`), and which targets survive.
   * Read fresh every time — the answer depends on today's components. The
   * re-scan dialog renders its failures, so they are not toasted globally.
   */
  getRescanOptions: async (scanId: string): Promise<RescanOptions> => {
    const { data } = await apiClient.get(`/scan/${scanId}/rescan-options/`)
    return data
  },

  /**
   * Run a scan again as a normal one-off scan. `version` is required; the two
   * flags default to false server-side.
   */
  rescan: async (scanId: string, payload: RescanPayload): Promise<RescanResult> => {
    const { data } = await apiClient.post(`/scan/${scanId}/rescan/`, payload)
    return data
  },

  /**
   * Get scan schedules list
   */
  getSchedules: async (): Promise<ScanScheduleResults> => {
    const { data } = await apiClient.get('/scan/schedules/')
    return data
  },

  /**
   * Get scan profiles list
   */
  getProfiles: async (params?: string): Promise<{ total_pages: number; current_page: number; results: ScanProfile[] }> => {
    const url = params ? `/scan-profiles/?${params}` : '/scan-profiles/'
    const { data } = await apiClient.get(url)
    return data
  },

  /**
   * Get scan overview/statistics
   */
  getOverview: async (): Promise<{ data: any }> => {
    const { data } = await apiClient.get('/scan/overview/')
    return data
  },

  /**
   * Get scan filter options
   */
  getFilterOptions: async (key: string): Promise<OptionsType> => {
    const { data } = await apiClient.get(`/scan/filter/?key=${key}`)
    return data
  },

  /**
   * Get scan schedule calendar for date range
   */
  getScheduleCalendar: async (startDate: string, endDate: string): Promise<ScanScheduleResults> => {
    const { data } = await apiClient.get(
      `/scan-schedule/?start_time=${startDate}T00:00&end_time=${endDate}T23:59`
    )
    return data
  },

  /**
   * Get workflows for scan creation with pagination.
   *
   * `portType` is the asset's own `port_type`, sent verbatim (`domain`, `ip`,
   * `IPRange`, `link`, `apk`). The backend matches it exactly and drops unknown
   * query params silently, so never send the retired `asset_type`.
   */
  getWorkflowsForScan: async (
    page: number,
    search = '',
    filters: { tags?: string[]; status?: string[]; entry?: string[] } = {},
    portType?: string
  ): Promise<{ total_pages: number; current_page: number; count?: number; results: ScanProfile[] }> => {
    const params: Record<string, any> = { page_size: 10, page }
    if (search) params.search = search
    if (filters.tags?.length) params.tags = filters.tags.join(',')
    if (filters.status?.length) params.status = filters.status.join(',')
    if (filters.entry?.length) params.entry = filters.entry.join(',')
    if (portType) params.port_type = portType
    const { data } = await apiClient.get(`/workflow/`, { params })
    return data
  },

  /**
   * Get all scannable assets (hosts, IPs, endpoints, files) for scan creation
   */
  getScannableAssets: async (page = 1, q = '', portType?: string): Promise<TargetListResponse> => {
    const { data } = await apiClient.get('/scannable-assets/', {
      params: { page_size: 10, page, ...(q ? { q } : {}), ...(portType ? { port_type: portType } : {}) },
    })
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  // Used by workflow↔target compatibility filtering + verify-before-scan gate in CreateScan.tsx.
  getWorkflowAvailableTargets: async (workflowId: string, page = 1, q = ''): Promise<TargetListResponse> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/available-targets/`, {
      params: { page_size: 10, page, ...(q ? { q } : {}) },
    })
    return data
  },

  /**
   * Create a new scan over one OR MANY targets.
   *
   * @param payload - `workflow`, optional `allow_inactive` (the user's "Run
   * anyway" confirmation past the orchestrator's inactive-component
   * rejection), and the target: exactly one of `seeds` (1..500 seed strings
   * naming scannable assets that ALREADY EXIST, all of one type) or `file_id`
   * (a confirmed uploaded APK, which is itself the target). Mutually exclusive
   * — the backend 400s if both or neither are sent. See scanTarget.ts for the
   * FE-side rule.
   *
   * The scalar `seed` is GONE with no alias: a single-target launch sends a
   * one-element list. Three things this endpoint will not do — create an asset
   * from an unknown seed (import it first), accept a mixed-type list, or report
   * progress per target. Quota is one unit per target, checked up front, and a
   * successful launch may come back `queued` while the orchestrator trigger is
   * retried for ~15 minutes; that is starting, not failing.
   */
  create: async (
    payload: { workflow: string; allow_inactive?: boolean } & ({ seeds: string[] } | { file_id: string })
  ): Promise<ScanResult> => {
    const { data } = await apiClient.post('/scan/', payload, OWNS_COMPONENT_REFUSAL)
    return data
  },

  /**
   * Schedule a scan. `ScanCreateScheduleSerializer` has the same seeds XOR
   * file_id contract as the immediate-scan endpoint (see `create` above and
   * scanTarget.ts) — exactly one of the two, never both.
   *
   * A schedule costs no quota at creation: each fired occurrence charges one
   * unit per target and refunds them if it fails before reaching the
   * orchestrator.
   */
  scheduleScan: async (
    payload: { workflow: string; rate: string; start_time: string | null } & ({ seeds: string[] } | { file_id: string })
  ): Promise<any> => {
    const { data } = await apiClient.post('/scan/schedule-scan/', payload, OWNS_COMPONENT_REFUSAL)
    return data
  },

  /**
   * Update scan details
   */
  update: async (scanId: string, payload: any): Promise<ScanResult> => {
    const { data } = await apiClient.patch(`/scan/${scanId}/`, payload)
    return data
  },

  /**
   * Delete a scan
   */
  delete: async (scanId: string): Promise<void> => {
    await apiClient.delete(`/scan/${scanId}/`)
  },

  /**
   * Stop a running scan
   */
  stop: async (scanId: string): Promise<void> => {
    await apiClient.post(`/scan/${scanId}/stop/`, {})
  },

  /**
   * Stop a scheduled scan
   */
  stopSchedule: async (scheduleId: string): Promise<void> => {
    await apiClient.post(`/scan-schedule/${scheduleId}/stop/`)
  },

  /**
   * Get filtered vulnerabilities for scans
   */
  getVulnerabilitiesFiltered: async (params?: string): Promise<any> => {
    const url = params ? `/scan/vulns-filter/?${params}` : '/scan/vulns-filter/'
    const { data } = await apiClient.get(url)
    return data
  },

  /** Discoveries produced by a single orchestrator node within a scan. */
  getNodeResult: async (scanId: string, nodeId: string): Promise<ScanNodeResult> => {
    const { data } = await apiClient.get<ScanNodeResult>(`/scans/${scanId}/nodes/${nodeId}/result/`)
    return data
  },
}
